import { z } from "zod";

import { LogprobsContentSchema } from "../completions/completions.models";

export const ResponseFormatTextSchema = z.object({ type: z.literal("text") });
export const ResponseFormatTextJSONSchemaConfigParamSchema = z.object({
  type: z.literal("json_schema"),
  name: z.string(),
  schema: z.record(z.string(), z.any()),
  description: z.string().optional(),
  strict: z.boolean().optional(),
});

export const ResponseTextConfigParamSchema = z
  .object({
    format: z
      .union([
        ResponseFormatTextSchema,
        ResponseFormatTextJSONSchemaConfigParamSchema,
      ])
      .optional(),
    verbosity: z.enum(["low", "medium", "high"]).optional(),
  })
  .partial();

export const ResponseInputContentTextSchema = z.object({
  type: z.literal("input_text"),
  text: z.string(),
});

export const ResponseInputContentImageSchema = z.object({
  type: z.literal("input_image"),
  image_url: z.string().optional(),
  file_id: z.string().optional(),
  // Optional on the wire; OpenAI defaults it to "auto".
  detail: z.enum(["low", "high", "auto"]).optional(),
});

export const ResponseInputContentFileSchema = z.object({
  type: z.literal("input_file"),
  file_id: z.string(),
});

// Assistant turns replayed in the input carry output_text parts, mirroring
// how the Responses API emitted them.
export const ResponseInputContentOutputTextSchema = z.object({
  type: z.literal("output_text"),
  text: z.string(),
});

// Catchall for content part types this schema does not model yet (e.g.
// input_audio, refusal). Without it, one unmodeled part fails the whole
// message schema and the item-level catchall then swallows the entire turn.
// Consumers must guard field access (`typeof part.text === "string"`) since
// a part's `type` literal alone does not prove a member's fields.
export const ResponseInputContentUnknownPartSchema = z
  .object({
    type: z.string(),
  })
  .passthrough();

export const ResponseInputMessageSchema = z.object({
  type: z.literal("message").optional(),
  role: z.enum(["user", "assistant", "system", "developer"]),
  content: z.union([
    z.string(),
    z.array(
      z.union([
        ResponseInputContentTextSchema,
        ResponseInputContentImageSchema,
        ResponseInputContentFileSchema,
        ResponseInputContentOutputTextSchema,
        ResponseInputContentUnknownPartSchema,
      ]),
    ),
  ]),
});

export type ResponseInputMessage = z.infer<typeof ResponseInputMessageSchema>;

// Agentic history items replayed in the input: a prior turn's tool call, its
// result, and reasoning items (which carry no convertible content but must
// not fail parsing).
export const ResponseInputFunctionCallSchema = z
  .object({
    type: z.literal("function_call"),
    call_id: z.string(),
    name: z.string(),
    arguments: z.string(),
    id: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough();

export type ResponseInputFunctionCall = z.infer<
  typeof ResponseInputFunctionCallSchema
>;

export const ResponseInputFunctionCallOutputSchema = z
  .object({
    type: z.literal("function_call_output"),
    call_id: z.string(),
    output: z.union([z.string(), z.array(z.any())]),
    id: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough();

export type ResponseInputFunctionCallOutput = z.infer<
  typeof ResponseInputFunctionCallOutputSchema
>;

export const ResponseInputReasoningSchema = z
  .object({
    type: z.literal("reasoning"),
  })
  .passthrough();

// Catchall for input item types this schema does not model yet (e.g.
// web_search_call, item_reference). OpenAI adds item types regularly, and
// an unknown item must degrade to a consumer-side skip rather than failing
// the whole request parse — that drift class previously dead-lettered every
// Responses payload carrying a then-unmodeled item.
export const ResponseInputUnknownItemSchema = z
  .object({
    type: z.string(),
  })
  .passthrough();

export type ResponseInputUnknownItem = z.infer<
  typeof ResponseInputUnknownItemSchema
>;

export const ResponseInputItemSchema = z.union([
  ResponseInputMessageSchema,
  ResponseInputFunctionCallSchema,
  ResponseInputFunctionCallOutputSchema,
  ResponseInputReasoningSchema,
  ResponseInputUnknownItemSchema,
]);

export type ResponseInputItem = z.infer<typeof ResponseInputItemSchema>;

/** Function tool definition in a Responses request's flat `tools` array
 * (name/parameters sit on the tool itself, unlike Chat Completions' nested
 * `function` object). */
export const ResponseFunctionToolSchema = z
  .object({
    type: z.literal("function"),
    name: z.string(),
    description: z.string().nullish(),
    parameters: z.record(z.string(), z.any()).nullish(),
    strict: z.boolean().nullish(),
  })
  .passthrough();

export type ResponseFunctionTool = z.infer<typeof ResponseFunctionToolSchema>;

export const V1ResponsesCreateRequestSchema = z
  .object({
    model: z.string(),
    input: z.union([z.string(), z.array(ResponseInputItemSchema)]),
    /** System/developer prompt applied to the response. */
    instructions: z.string().nullish(),
    /** Prior response this request continues; the earlier turns live in
     * OpenAI's stored response state, not in this payload. */
    previous_response_id: z.string().nullish(),
    temperature: z.number().optional(),
    top_p: z.number().optional(),
    max_output_tokens: z.number().optional(),
    top_logprobs: z.number().optional(),
    include: z.array(z.string()).optional(),
    user: z.string().optional(),
    stream: z.boolean().optional(),
    text: ResponseTextConfigParamSchema.optional(),
    tools: z.array(z.any()).optional(),
    tool_choice: z.any().optional(),
    parallel_tool_calls: z.boolean().optional(),
    prompt_cache_key: z.unknown().optional(),
    prompt_cache_retention: z.unknown().optional(),
    reasoning: z
      .object({
        effort: z.string().optional(),
      })
      .passthrough()
      .nullish(),
    metadata: z.record(z.string(), z.any()).optional(),
  })
  .passthrough();

export type V1ResponsesCreateRequest = z.infer<
  typeof V1ResponsesCreateRequestSchema
>;

export const ResponseUsageSchema = z.object({
  input_tokens: z.number(),
  output_tokens: z.number(),
  total_tokens: z.number(),
  input_tokens_details: z
    .object({
      cached_tokens: z.number(),
    })
    .optional(),
  output_tokens_details: z
    .object({
      reasoning_tokens: z.number().optional(),
    })
    .optional(),
});

export type ResponseUsage = z.infer<typeof ResponseUsageSchema>;

export const ResponseOutputTextSchema = z.object({
  type: z.literal("output_text"),
  text: z.string(),
  annotations: z.array(z.any()).default([]),
  logprobs: z.array(LogprobsContentSchema).nullable().optional(),
});

// Catchall for message content part types this schema does not model yet
// (e.g. refusal). Without it, one unmodeled part fails the message schema
// and the item-level catchall then swallows the whole turn — losing the
// output_text that was extractable. Consumers filter by `type` so unknown
// parts are ignored, not misread.
export const ResponseOutputContentUnknownPartSchema = z
  .object({
    type: z.string(),
  })
  .passthrough();

// Final output message: allow message vs reasoning to carry their native content shapes
export const ResponseOutputMessageSchema = z.object({
  id: z.string(),
  type: z.literal("message"),
  role: z.literal("assistant"),
  // Status may be omitted by providers; do not default
  status: z
    .union([z.enum(["in_progress", "completed", "incomplete"]), z.string()])
    .optional(),
  content: z.array(
    z.union([ResponseOutputTextSchema, ResponseOutputContentUnknownPartSchema]),
  ),
});

export type ResponseOutputMessage = z.infer<typeof ResponseOutputMessageSchema>;

export const ResponseOutputReasoningSchema = z.object({
  id: z.string(),
  type: z.literal("reasoning"),
  // No status in OpenAI examples for reasoning outputs
  content: z.array(
    z.object({ type: z.literal("reasoning_text"), text: z.string() }),
  ),
});

// Shared base for function call output items
export const FunctionCallItemSchema = z.object({
  id: z.string().default(""),
  type: z.literal("function_call"),
  call_id: z.string(),
  name: z.string(),
  arguments: z.string(),
  status: z
    .union([z.enum(["in_progress", "completed", "incomplete"]), z.string()])
    .optional(),
});

// Custom / freeform (grammar) tool call output item (OpenAI GPT-5.x custom
// tools). Like function_call, but carries a freeform ``input`` string instead
// of a JSON ``arguments`` string. Assembled into the response output so the
// tool call isn't dropped from the final result.
export const CustomToolCallItemSchema = z.object({
  id: z.string().default(""),
  type: z.literal("custom_tool_call"),
  call_id: z.string(),
  name: z.string(),
  input: z.string(),
  status: z
    .union([z.enum(["in_progress", "completed", "incomplete"]), z.string()])
    .optional(),
});

// OpenAI hosted web-search tool output item (GPT-5.x ``web_search`` tool).
// The finalized item carries the whole ``action`` payload — ``search`` (with
// ``query``/``queries``), ``open_page``/``find`` (with ``url``/``pattern``) —
// so the per-chunk ``response.web_search_call.*`` progress events need no
// assembly. OpenAI adds action fields regularly: model the known ones and
// passthrough the rest rather than risk dropping the item.
export const WebSearchCallActionSchema = z
  .object({
    type: z.union([z.enum(["search", "open_page", "find"]), z.string()]),
    query: z.string().optional(),
    queries: z.array(z.string()).optional(),
    url: z.string().optional(),
    pattern: z.string().optional(),
  })
  .passthrough();

export const WebSearchCallItemSchema = z.object({
  id: z.string().default(""),
  type: z.literal("web_search_call"),
  status: z
    .union([
      z.enum(["in_progress", "searching", "completed", "incomplete"]),
      z.string(),
    ])
    .optional(),
  action: WebSearchCallActionSchema.optional(),
});

// Catchall for output item types this schema does not model yet. OpenAI
// adds item types regularly (custom_tool_call had to be retrofitted), and an
// unknown item must degrade to a consumer-side skip/log rather than failing
// the whole response parse — the same drift class that previously
// dead-lettered request parsing. Consumers must guard field access, since a
// `type` literal alone does not prove a member's fields.
export const ResponseOutputUnknownItemSchema = z
  .object({
    type: z.string(),
  })
  .passthrough();

export type ResponseOutputUnknownItem = z.infer<
  typeof ResponseOutputUnknownItemSchema
>;

export const ResponseOutputSchema = z.union([
  ResponseOutputMessageSchema,
  ResponseOutputReasoningSchema,
  FunctionCallItemSchema,
  CustomToolCallItemSchema,
  WebSearchCallItemSchema,
  ResponseOutputUnknownItemSchema,
]);

export type ResponseOutput = z.infer<typeof ResponseOutputSchema>;

export const V1ResponsesResponseSchema = z.object({
  id: z.string(),
  object: z.literal("response"),
  created_at: z.number(),
  model: z.string(),
  output: z.array(ResponseOutputSchema),
  status: z
    .union([z.enum(["in_progress", "completed", "incomplete"]), z.string()])
    .default("completed"),
  // OpenAI returns additional details when status is "incomplete".
  // Providers send null when not applicable, so use .nullish().
  incomplete_details: z
    .object({
      reason: z.string(),
    })
    .nullish(),
  // Optional reasoning metadata in responses. Uses union of known values with
  // z.string() fallback so autocomplete works but unknown values don't break parsing.
  reasoning: z
    .object({
      effort: z
        .union([
          z.enum(["none", "minimal", "low", "medium", "high"]),
          z.string(),
        ])
        .nullable()
        .optional(),
      summary: z
        .union([z.enum(["auto", "concise", "detailed"]), z.string()])
        .nullable()
        .optional(),
    })
    .nullish(),
  parallel_tool_calls: z.boolean().nullish(),
  temperature: z.number().nullish(),
  top_p: z.number().nullish(),
  top_logprobs: z.number().nullish(),
  tool_choice: z.any().nullish(),
  tools: z.array(z.any()).nullish().default([]),
  max_output_tokens: z.number().nullish(),
  usage: ResponseUsageSchema.nullish(),
  text: ResponseTextConfigParamSchema.nullish(),
  metadata: z.record(z.string(), z.any()).nullish(),
  // Optional error field to align with OpenAI Responses shape
  error: z
    .object({
      message: z.string(),
      code: z.string(),
    })
    .nullish(),
});

export type V1ResponsesResponse = z.infer<typeof V1ResponsesResponseSchema>;

// Event data payloads. Each schema is the **wire shape** of one
// SSE event as emitted on the Responses-API stream — i.e. exactly what
// lands in the ``data:`` line after JSON parsing. The discriminator is
// the top-level ``type`` field.
//
// Earlier versions of this module wrapped these into a synthetic
// ``{event, data}`` shape to mirror the SSE ``event:`` header line, but
// the header is redundant with the ``type`` field (and some upstreams —
// LiteLLM bridging Azure Foundry's Responses API in particular — strip
// it entirely). Treating ``type`` as the discriminator makes the static
// type system match what's actually on the wire and removes a category
// of bugs where downstream code reads ``chunk.event`` against a
// header-less payload and silently drops the chunk.
export const ResponseCreatedEventDataSchema = z.object({
  type: z.literal("response.created"),
  sequence_number: z.number().int().nonnegative().optional(),
  response: V1ResponsesResponseSchema,
});

export const ResponseInProgressEventDataSchema = z.object({
  type: z.literal("response.in_progress"),
  sequence_number: z.number().int().nonnegative().optional(),
  response: V1ResponsesResponseSchema,
});

// OpenAI shape only: delta is a string; logprobs at top-level
export const ResponseOutputTextDeltaEventDataSchema = z
  .object({
    type: z.literal("response.output_text.delta"),
    sequence_number: z.number().int().nonnegative().optional(),
    output_index: z.number().int().nonnegative().optional(),
    content_index: z.number().int().nonnegative().optional(),
    item_id: z.string().optional(),
    delta: z.string(),
    logprobs: z.array(LogprobsContentSchema).optional(),
  })
  .passthrough();

export const ResponseCompletedEventDataSchema = z.object({
  type: z.literal("response.completed"),
  sequence_number: z.number().int().nonnegative().optional(),
  response: V1ResponsesResponseSchema,
});

// Terminal incomplete event (OpenAI streams status change when cut short)
export const ResponseIncompleteEventDataSchema = z.object({
  type: z.literal("response.incomplete"),
  sequence_number: z.number().int().nonnegative().optional(),
  response: V1ResponsesResponseSchema,
});

export const ResponseFailedEventDataSchema = z.object({
  type: z.literal("response.failed"),
  sequence_number: z.number().int().nonnegative().optional(),
  response: V1ResponsesResponseSchema,
});

// Additional Harmony event data schemas
export const OutputTextDoneEventDataSchema = z.object({
  type: z.literal("response.output_text.done"),
  sequence_number: z.number().int().nonnegative().optional(),
  output_index: z.number().int(),
  content_index: z.number().int(),
  item_id: z.string().optional(),
  text: z.string(),
  logprobs: z.array(LogprobsContentSchema).nullable().optional(),
});

export const ContentPartSchema = ResponseOutputTextSchema;

const ContentPartEventBaseSchema = z.object({
  sequence_number: z.number().int().nonnegative().optional(),
  output_index: z.number().int(),
  content_index: z.number().int(),
  item_id: z.string().optional(),
  part: ContentPartSchema,
});

export const ContentPartAddedEventDataSchema =
  ContentPartEventBaseSchema.extend({
    type: z.literal("response.content_part.added"),
  });

export const ContentPartDoneEventDataSchema = ContentPartEventBaseSchema.extend(
  { type: z.literal("response.content_part.done") },
);

export const ReasoningTextDeltaEventDataSchema = z.object({
  type: z.literal("response.reasoning_text.delta"),
  sequence_number: z.number().int().nonnegative().optional(),
  output_index: z.number().int(),
  content_index: z.number().int(),
  item_id: z.string().optional(),
  delta: z.string(),
});

export const ReasoningTextDoneEventDataSchema = z.object({
  type: z.literal("response.reasoning_text.done"),
  sequence_number: z.number().int().nonnegative().optional(),
  output_index: z.number().int(),
  content_index: z.number().int(),
  item_id: z.string().optional(),
  text: z.string(),
});

const ReasoningTextContentSchema = z.object({
  type: z.literal("reasoning_text"),
  text: z.string(),
});

const MessageItemSchema = z.object({
  id: z.string().optional().default(""),
  type: z.literal("message"),
  role: z.literal("assistant").optional().default("assistant"),
  status: z
    .union([z.enum(["in_progress", "completed", "incomplete"]), z.string()])
    .optional(),
  content: z.array(
    z.union([ResponseOutputTextSchema, ResponseOutputContentUnknownPartSchema]),
  ),
});

const ReasoningItemSchema = z.object({
  id: z.string().optional().default(""),
  type: z.literal("reasoning"),
  status: z
    .union([z.enum(["in_progress", "completed", "incomplete"]), z.string()])
    .optional(),
  content: z.array(ReasoningTextContentSchema),
});

const OutputItemEventBaseSchema = z.object({
  sequence_number: z.number().int().nonnegative().optional(),
  output_index: z.number().int(),
  // The unknown-item catchall keeps a stream event with a new item type
  // parseable; the proxy assembler's unhandled-item guard then logs it
  // instead of the whole event being rejected.
  item: z.union([
    MessageItemSchema,
    ReasoningItemSchema,
    FunctionCallItemSchema,
    CustomToolCallItemSchema,
    WebSearchCallItemSchema,
    ResponseOutputUnknownItemSchema,
  ]),
});

export const OutputItemAddedEventDataSchema = OutputItemEventBaseSchema.extend({
  type: z.literal("response.output_item.added"),
});

export const OutputItemDoneEventDataSchema = OutputItemEventBaseSchema.extend({
  type: z.literal("response.output_item.done"),
});

// Minimal schemas for the provider-dependent / partial-payload events
// that previously had ``data: z.any()`` in the wrapper. Each one pins
// the ``type`` literal — that's enough for the discriminated union to
// route correctly — and ``passthrough()`` keeps the rest of the JSON
// intact for any downstream consumer that wants the per-event fields.
// If a richer schema for one of these lands later, swap the inline
// definition for a named one without changing call sites.
const _passthroughEventSchema = <T extends string>(typeLiteral: T) =>
  z
    .object({
      type: z.literal(typeLiteral),
      sequence_number: z.number().int().nonnegative().optional(),
    })
    .passthrough();

export const ResponseFunctionCallArgumentsDeltaEventDataSchema =
  _passthroughEventSchema("response.function_call_arguments.delta");
export const ResponseFunctionCallArgumentsDoneEventDataSchema =
  _passthroughEventSchema("response.function_call_arguments.done");
export const ResponseCustomToolCallInputDeltaEventDataSchema =
  _passthroughEventSchema("response.custom_tool_call_input.delta");
export const ResponseCustomToolCallInputDoneEventDataSchema =
  _passthroughEventSchema("response.custom_tool_call_input.done");
export const ResponseReasoningSummaryDeltaEventDataSchema =
  _passthroughEventSchema("response.reasoning_summary.delta");
export const ResponseReasoningSummaryDoneEventDataSchema =
  _passthroughEventSchema("response.reasoning_summary.done");
// OpenAI's actual reasoning-summary stream events (GPT-5.x Responses API with
// ``reasoning: { summary: ... }``). The two ``response.reasoning_summary.*``
// names above are kept for compatibility, but the wire emits the
// ``_part``/``_text`` forms below — one ``_text.delta`` per streamed summary
// chunk, so an unmodeled name here floods the proxy's unknown-event fatal
// (INC-161, same class as INC-87's custom_tool_call_input events).
export const ResponseReasoningSummaryPartAddedEventDataSchema =
  _passthroughEventSchema("response.reasoning_summary_part.added");
export const ResponseReasoningSummaryPartDoneEventDataSchema =
  _passthroughEventSchema("response.reasoning_summary_part.done");
export const ResponseReasoningSummaryTextDeltaEventDataSchema =
  _passthroughEventSchema("response.reasoning_summary_text.delta");
export const ResponseReasoningSummaryTextDoneEventDataSchema =
  _passthroughEventSchema("response.reasoning_summary_text.done");
// OpenAI emits per-annotation events while streaming ``output_text`` that
// carries citations/URLs (e.g. GPT-5.x web-search or file-search grounded
// answers): one ``response.output_text.annotation.added`` per annotation.
// The finalized annotations arrive whole on the ``output_text`` part of the
// ``response.output_item.done`` message item, so these per-chunk events are
// progress noise; modeling them here keeps the stream assembler's
// unknown-event fatal quiet (same drift class as the reasoning_summary /
// web_search_call events — INC-87/INC-161).
export const ResponseOutputTextAnnotationAddedEventDataSchema =
  _passthroughEventSchema("response.output_text.annotation.added");
export const ResponseRefusalDeltaEventDataSchema = _passthroughEventSchema(
  "response.refusal.delta",
);
export const ResponseRefusalDoneEventDataSchema = _passthroughEventSchema(
  "response.refusal.done",
);
// OpenAI hosted web-search progress events (GPT-5.x ``web_search`` tool) —
// one per search phase per item. The finalized item arrives whole on
// ``response.output_item.done``, so these are per-chunk progress noise;
// modeling them here keeps the stream assembler's unknown-event fatal quiet
// (same drift class as the reasoning_summary / custom_tool_call_input events).
export const ResponseWebSearchCallInProgressEventDataSchema =
  _passthroughEventSchema("response.web_search_call.in_progress");
export const ResponseWebSearchCallSearchingEventDataSchema =
  _passthroughEventSchema("response.web_search_call.searching");
export const ResponseWebSearchCallCompletedEventDataSchema =
  _passthroughEventSchema("response.web_search_call.completed");
export const ResponseOutputItemDeltaEventDataSchema = _passthroughEventSchema(
  "response.output_item.delta",
);
export const ResponseOutputItemCompletedEventDataSchema =
  _passthroughEventSchema("response.output_item.completed");

/**
 * Wire shape of one SSE chunk on the Responses-API stream — discriminated
 * by the top-level ``type`` field that every event carries.
 *
 * This is the actual JSON shape that lands in ``data:`` lines, e.g.
 * ``{"type":"response.completed","response":{...}}``. The proxy used to
 * wrap chunks in a synthetic ``{event, data}`` form (mirroring the SSE
 * ``event:`` header line) but the header is redundant with ``type``, and
 * some upstreams strip the header — wrapping made the type system
 * disagree with the wire and let chunks fall through dispatchers
 * silently. Discriminating on ``type`` directly matches the wire.
 */
export const ResponseStreamChunkSchema = z.discriminatedUnion("type", [
  ResponseCreatedEventDataSchema,
  ResponseInProgressEventDataSchema,
  ResponseIncompleteEventDataSchema,
  ResponseFailedEventDataSchema,
  ResponseCompletedEventDataSchema,
  ResponseOutputTextDeltaEventDataSchema,
  OutputTextDoneEventDataSchema,
  ContentPartAddedEventDataSchema,
  ContentPartDoneEventDataSchema,
  OutputItemAddedEventDataSchema,
  OutputItemDoneEventDataSchema,
  ResponseOutputItemDeltaEventDataSchema,
  ResponseOutputItemCompletedEventDataSchema,
  ReasoningTextDeltaEventDataSchema,
  ReasoningTextDoneEventDataSchema,
  ResponseFunctionCallArgumentsDeltaEventDataSchema,
  ResponseFunctionCallArgumentsDoneEventDataSchema,
  ResponseCustomToolCallInputDeltaEventDataSchema,
  ResponseCustomToolCallInputDoneEventDataSchema,
  ResponseReasoningSummaryDeltaEventDataSchema,
  ResponseReasoningSummaryDoneEventDataSchema,
  ResponseReasoningSummaryPartAddedEventDataSchema,
  ResponseReasoningSummaryPartDoneEventDataSchema,
  ResponseReasoningSummaryTextDeltaEventDataSchema,
  ResponseReasoningSummaryTextDoneEventDataSchema,
  ResponseRefusalDeltaEventDataSchema,
  ResponseRefusalDoneEventDataSchema,
  ResponseWebSearchCallInProgressEventDataSchema,
  ResponseWebSearchCallSearchingEventDataSchema,
  ResponseWebSearchCallCompletedEventDataSchema,
  ResponseOutputTextAnnotationAddedEventDataSchema,
]);

export type ResponseStreamChunk = z.infer<typeof ResponseStreamChunkSchema>;
